import { ChangeDetectionStrategy, Component, ViewChild, inject, input, output, OnInit, DestroyRef } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { ComponentsModule, DrawerComponent, MaskService } from '../../../../lib';
import { RoomByRoomCommon } from '../../common';
import { CalculationBase } from '../../models';
import { NumberPipe } from '../../pipes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ca-rbr-calculation-base',
    templateUrl: './calculation-base.component.html',
    styleUrls: ['./calculation-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DecimalPipe],
    imports: [TranslatePipe, ComponentsModule, ReactiveFormsModule, MatInputModule, NumberPipe]
})
export class CalculationBaseComponent implements OnInit {
    headerText = input<string>('RBR.CALCULATION_BASE.TITLE');
    calculationBase = input.required<CalculationBase>();
    calculatedHeatLoadFactor = input<number | undefined>(undefined);
    calculatedCoolingLoadFactor = input<number | undefined>(undefined);
    confirm = output<CalculationBase>();
    maskService = inject(MaskService);
    mask = this.maskService.getNumericMask({ separatorLimit: '1000', suffix: 'MEASUREMENT_UNITS.M2' });
    loadFactorMask = this.maskService.getNumericMask({ separatorLimit: '1000', numberOfDecimals: '2' });
    // The reference-unit label is intentionally rendered as a literal because the shared translation
    // source does not (yet) provide a MEASUREMENT_UNITS key for W/m²; W/m² is a unit that is identical across locales.
    readonly loadFactorSuffix = ' W/m²';
    @ViewChild('drawer') drawer!: DrawerComponent;

    readonly form = new FormGroup({
        totalLivingArea: new FormControl<number>(0, {
            validators: [RoomByRoomCommon.requiredValidTotalLivingArea],
            nonNullable: true
        }),
        includeTotalBuilding: new FormControl<boolean>(false, { nonNullable: true }),
        setCustomLoadFactors: new FormControl<boolean>(false, { nonNullable: true }),
        customHeatLoadFactor: new FormControl<number | null>(null),
        customCoolingLoadFactor: new FormControl<number | null>(null)
    });

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.form.controls.includeTotalBuilding.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
            this.toggleTotalLivingArea(value);
        });

        this.form.controls.setCustomLoadFactors.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
            this.toggleCustomLoadFactors(value);
        });
    }

    hasError(controlName: string, errorKey: string): boolean {
        const control = this.form.get(controlName);

        if (!control) {
            return false;
        }

        return control.hasError(errorKey) && (control.touched || control.dirty);
    }

    openDrawer(): void {
        const value = this.calculationBase();
        const hasCustomLoadFactors = value.standardRoomHeatLoad != null || value.standardRoomCoolingLoad != null;

        this.form.reset({
            totalLivingArea: value.totalLivingArea,
            includeTotalBuilding: value.includeTotalBuilding,
            setCustomLoadFactors: hasCustomLoadFactors,
            customHeatLoadFactor: value.standardRoomHeatLoad ?? null,
            customCoolingLoadFactor: value.standardRoomCoolingLoad ?? null
        });

        this.toggleTotalLivingArea(value.includeTotalBuilding);
        this.toggleCustomLoadFactors(hasCustomLoadFactors);

        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.drawer.open();
    }

    onCancel(): void {
        this.drawer.close();
    }

    onConfirm(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const { totalLivingArea, includeTotalBuilding, setCustomLoadFactors, customHeatLoadFactor, customCoolingLoadFactor } =
            this.form.getRawValue();

        const payload: CalculationBase = {
            totalLivingArea: totalLivingArea || undefined,
            includeTotalBuilding,
            standardRoomHeatLoad: setCustomLoadFactors ? this.normalizeLoadFactor(customHeatLoadFactor) : undefined,
            standardRoomCoolingLoad: setCustomLoadFactors ? this.normalizeLoadFactor(customCoolingLoadFactor) : undefined
        };

        this.confirm.emit(payload);
        this.drawer.close();
    }

    private toggleTotalLivingArea(enable: boolean): void {
        if (enable) {
            this.form.controls.totalLivingArea.enable();
        } else {
            this.form.controls.totalLivingArea.disable();
        }
    }

    private toggleCustomLoadFactors(enable: boolean): void {
        if (enable) {
            this.form.controls.customHeatLoadFactor.enable();
            this.form.controls.customCoolingLoadFactor.enable();
        } else {
            this.form.controls.customHeatLoadFactor.reset(null);
            this.form.controls.customCoolingLoadFactor.reset(null);
            this.form.controls.customHeatLoadFactor.disable();
            this.form.controls.customCoolingLoadFactor.disable();
        }
    }

    private normalizeLoadFactor(value: number | null): number | undefined {
        const normalized = RoomByRoomCommon.toNumber(value);
        return normalized ?? undefined;
    }
}
